// Define the FilterType enum
export const FilterType = {
	EQUALS: 'EQUALS',
	CONTAINS: 'CONTAINS',
	STARTS_WITH: 'STARTS_WITH',
	ENDS_WITH: 'ENDS_WITH',
	GREATER_THAN: 'GREATER_THAN',
	LESS_THAN: 'LESS_THAN',
	DATE_EQUALS: 'DATE_EQUALS',
	DATE_BEFORE: 'DATE_BEFORE',
	DATE_AFTER: 'DATE_AFTER'
};

class FilterHelper {
	/**
	 * Converts epoch value to a Date object, handling both milliseconds and seconds formats.
	 * @param {number|string} value - The epoch value to convert.
	 * @returns {Date} - The Date object.
	 */
	static toEpochTime(value) {
		if (!value) {
			return NaN;
		}
		let dateValue;
		if (value instanceof Date) {
			dateValue = value;
		} else if (typeof value === 'number') {
			dateValue = new Date(value < 10000000000 ? value * 1000 : value); // Convert seconds to milliseconds if needed
		} else if (typeof value === 'string') {
			dateValue = new Date(value);
			// If the date is invalid, explicitly return NaN to indicate failure
			if (isNaN(dateValue.getTime())) {
				return NaN;
			}
		} else {
			return NaN;
		}

		return dateValue.getTime(); // Return the epoch time in milliseconds
	}

	/**
	 * Filters an array of objects based on a filter object.
	 * @param {Array} items - The array of objects to be filtered.
	 * @param {Object} filter - The filter object containing filter conditions.
	 * @returns {Array} - The filtered array.
	 */
	static applyFilters(items, filter) {
		if (!filter || !items || !Array.isArray(items)) return items;

		return items.filter((item) => {
			let includeItem = true;

			Object.keys(filter).forEach((key) => {
				if (!includeItem) return; // Exit early if the item has already been excluded

				const filterValue = filter[key].value;
				const filterType = filter[key].type;
				const itemValue = item[key];

				if (filterValue === '' || filterValue === undefined) {
					return; // Skip filtering for this field, but do not exclude the item
				}

				// Parse filter and item values as dates for date-related comparisons
				const itemDate = this.toEpochTime(itemValue);
				const filterDate = this.toEpochTime(filterValue);

				switch (filterType) {
					case FilterType.EQUALS:
						if (itemValue !== filterValue) includeItem = false;
						break;
					case FilterType.CONTAINS:
						if (!itemValue || !itemValue.toString().includes(filterValue)) includeItem = false;
						break;
					case FilterType.STARTS_WITH:
						if (!itemValue || !itemValue.toString().startsWith(filterValue)) includeItem = false;
						break;
					case FilterType.ENDS_WITH:
						if (!itemValue || !itemValue.toString().endsWith(filterValue)) includeItem = false;
						break;
					case FilterType.GREATER_THAN:
						if (!(itemValue > filterValue)) includeItem = false;
						break;
					case FilterType.LESS_THAN:
						if (!(itemValue < filterValue)) includeItem = false;
						break;
					case FilterType.DATE_EQUALS:
						if (isNaN(itemDate) || isNaN(filterDate) || itemDate !== filterDate()) {
							includeItem = false;
						}
						break;
					case FilterType.DATE_BEFORE:
						if (isNaN(itemDate) || isNaN(filterDate) || itemDate >= filterDate) {
							includeItem = false;
						}
						break;
					case FilterType.DATE_AFTER:
						if (isNaN(itemDate) || isNaN(filterDate) || itemDate <= filterDate) {
							includeItem = false;
						}
						break;
					default:
						break; // If filter type is not recognized, do nothing (include item)
				}
			});

			return includeItem;
		});
	}
}

export default FilterHelper;
